import { useState } from "react";
import { ApiClientError } from "../../lib/api/client";
import { useCreateDonorFundMutation, useCreatePledgeMutation } from "../../lib/api/donors";
import { Button } from "../../components/ui/Button";
import { Modal } from "../../components/ui/Modal";

interface DonorFundModalProps {
  open: boolean;
  onClose: () => void;
  donorId: string;
  donorName: string;
  units: { id: string; name: string; code: string }[];
  funds: { id: string; name: string }[];
  mode: "fund" | "pledge";
}

export function DonorFundModal({
  open,
  onClose,
  donorId,
  donorName,
  units,
  funds,
  mode,
}: DonorFundModalProps) {
  const createFund = useCreateDonorFundMutation(donorId);
  const createPledge = useCreatePledgeMutation(donorId);
  const [name, setName] = useState("");
  const [unitIds, setUnitIds] = useState<string[]>(units.map((unit) => unit.id));
  const [fundId, setFundId] = useState(funds[0]?.id ?? "");
  const [amount, setAmount] = useState("");
  const [period, setPeriod] = useState("2026–27");
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    setError(null);
    try {
      if (mode === "fund") {
        await createFund.mutateAsync({
          name,
          restricted: true,
          unitIds,
        });
      } else {
        await createPledge.mutateAsync({
          fundId,
          amount: Number.parseFloat(amount).toFixed(2),
          period,
        });
      }
      onClose();
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : "Could not save.");
    }
  }

  const pending = createFund.isPending || createPledge.isPending;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={mode === "fund" ? "Add restricted fund" : "Record pledge"}
      description={`${donorName} · ${mode === "fund" ? "Link programme units to this donor fund." : "Pledge is a commitment, not cash received."}`}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={pending} onClick={() => void submit()}>
            {pending ? "Saving…" : mode === "fund" ? "Create fund" : "Save pledge"}
          </Button>
        </>
      }
    >
      <div className="grid gap-4">
        {error ? (
          <p className="edlab-error">
            {error}
          </p>
        ) : null}
        {mode === "fund" ? (
          <>
            <label className="grid gap-1 text-sm">
              Fund name
              <input
                className="edlab-input"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                }}
              />
            </label>
            <fieldset className="grid gap-2 text-sm">
              <legend className="font-medium">Programme units</legend>
              {units.map((unit) => (
                <label key={unit.id} className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={unitIds.includes(unit.id)}
                    onChange={(e) => {
                      setUnitIds((current) =>
                        e.target.checked
                          ? [...current, unit.id]
                          : current.filter((id) => id !== unit.id),
                      );
                    }}
                  />
                  {unit.name}
                </label>
              ))}
            </fieldset>
          </>
        ) : (
          <>
            <label className="grid gap-1 text-sm">
              Fund
              <select
                className="edlab-input"
                value={fundId}
                onChange={(e) => {
                  setFundId(e.target.value);
                }}
              >
                {funds.map((fund) => (
                  <option key={fund.id} value={fund.id}>
                    {fund.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="grid gap-1 text-sm">
              Pledge amount
              <input
                type="number"
                min="0"
                step="0.01"
                className="edlab-input"
                value={amount}
                onChange={(e) => {
                  setAmount(e.target.value);
                }}
              />
            </label>
            <label className="grid gap-1 text-sm">
              Period
              <input
                className="edlab-input"
                value={period}
                onChange={(e) => {
                  setPeriod(e.target.value);
                }}
              />
            </label>
          </>
        )}
      </div>
    </Modal>
  );
}
